import React, { useEffect, useState } from 'react';

export interface ToastProps {
    message: string;
    type?: 'success' | 'error' | 'info' | 'warning';
    duration?: number;
    onClose: () => void;
}

const FADE_OUT_DURATION = 300; // ms for fade-out animation

const Toast: React.FC<ToastProps> = ({
    message,
    type = 'success',
    duration = 3000,
    onClose
}) => {
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, FADE_OUT_DURATION);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration]);

    const bgColorClass = {
        success: 'bg-green-500 dark:bg-green-600',
        error: 'bg-red-500 dark:bg-red-600',
        info: 'bg-blue-500 dark:bg-blue-600',
        warning: 'bg-yellow-500 dark:bg-yellow-600'
    }[type];

    const iconMap = {
        success: '✓',
        error: '✕',
        info: 'i',
        warning: '⚠'
    };

    return (
        <div
            className={`fixed top-4 right-4 z-50 transition-opacity duration-300 ${
                isClosing ? 'opacity-0' : 'opacity-100 animate-slide-in-right'
            }`}
        >
            <div className={`${bgColorClass} text-white px-6 py-4 rounded-lg shadow-lg dark:shadow-2xl flex items-center gap-3 min-w-[300px] max-w-md`}>
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-white dark:bg-gray-900 bg-opacity-20 dark:bg-opacity-30 rounded-full font-bold">
                    {iconMap[type]}
                </div>
                <p className="flex-1 font-medium">{message}</p>
                <button
                    onClick={handleClose}
                    className="flex-shrink-0 text-white hover:text-gray-200 dark:hover:text-gray-300 transition-colors"
                    aria-label="Close notification"
                >
                    ✕
                </button>
            </div>
        </div>
    );
};

export default Toast;
